import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { AuthService, ICurrentUser } from '@service/auth.service';
import { AccessSetting, AccessType, UserAccess } from 'ops/common/models/access-settings';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-user-share-settings',
    templateUrl: './user-share-settings.component.html',
    styleUrls: ['./user-share-settings.component.scss'],
    standalone: false
})
export class UserShareSettingsComponent implements OnInit, OnDestroy, OnChanges {
    @Input() role: string;
    @Input() roleId: string;
    @Input() accessLevel: AccessType;
    @Input() inViewPage: boolean;
    @Input() optionsDisabled: boolean;
    @Input() isCreatingPage: boolean;
    @Output() onUserAccessChanged: EventEmitter<UserAccess> = new EventEmitter<UserAccess>();
    @ViewChild('optionElementRef') optionElementRef: ElementRef;

    accessTypeTile = 'user-access.label';

    optionsOpened: boolean = false;
    currentAccess: AccessType = AccessType.NoAccess;
    currentIcon: string = 'lock';

    accessSettingTypeArray: AccessSetting[] = [
        { accessType: AccessType.NoAccess, icon: 'lock' },
        { accessType: AccessType.View, icon: 'view-access' },
        { accessType: AccessType.Edit, icon: 'edit-access' },
    ];

    currentUserChanged: Subscription;

    constructor(private authService: AuthService) {}
    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (!this.optionElementRef?.nativeElement.contains(event.target) && this.optionsOpened) {
            this.optionsOpened = false;
        }
    }

    ngOnInit() {
        this.currentUserChanged = this.authService.currentUser$.subscribe(() => {
            if (this.inViewPage) {
                return;
            }
            const currentUserId = this.authService.getCurrentUserId();

            if (this.isCreatingPage && currentUserId === this.roleId) {
                this.accessLevel = AccessType.Owner;
                this.onUserAccessChanged.emit({
                    roleId: this.roleId,
                    accessLevel: AccessType.Owner,
                });
            } else if (
                this.isCreatingPage &&
                currentUserId !== this.roleId &&
                this.accessLevel === AccessType.Owner
            ) {
                this.accessLevel = AccessType.NoAccess;
                this.changeAccess({ accessType: AccessType.NoAccess, icon: 'lock' });
            }
        });
        const currentUserId = this.authService.getCurrentUserId();

        if (this.optionsDisabled && currentUserId === this.roleId) {
            this.accessSettingTypeArray = [
                { accessType: AccessType.NoAccess, icon: 'lock' },
                { accessType: AccessType.View, icon: 'view-access' },
            ];
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.accessLevel && this.accessLevel) {
            if (this.accessLevel !== AccessType.Owner) {
                this.currentAccess = this.accessLevel;
                if (this.accessLevel === AccessType.Edit) {
                    this.currentIcon = 'edit-access';
                } else if (this.accessLevel === AccessType.View) {
                    this.currentIcon = 'view-access';
                } else {
                    this.currentIcon = 'lock';
                }
            }
        }
    }

    ngOnDestroy(): void {
        this.currentUserChanged?.unsubscribe();
    }

    openOptions() {
        if (this.shouldDisableOption()) {
            return;
        }
        this.optionsOpened = !this.optionsOpened;
    }

    changeAccess(accessSetting: AccessSetting) {
        this.currentAccess = accessSetting.accessType;
        this.currentIcon = accessSetting.icon;
        this.onUserAccessChanged.emit({
            roleId: this.roleId,
            accessLevel: accessSetting.accessType,
        });
        this.optionsOpened = false;
    }

    isOwner() {
        return this.accessLevel === AccessType.Owner;
    }

    shouldDisableOption() {
        const currentUserId = this.authService.getCurrentUserId();
        if (this.optionsDisabled && currentUserId !== this.roleId) {
            return true;
        }
        return false;
    }
}
